import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import LeadsPage from "@/pages/LeadsPage";
import ProposalsPage from "@/pages/ProposalsPage";
import ClientsPage from "@/pages/ClientsPage";
import EmployeesPage from "@/pages/EmployeesPage";
import ContractsPage from "@/pages/ContractsPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import TasksPage from "@/pages/TasksPage";
import DocumentsPage from "@/pages/DocumentsPage";
import TimesheetsPage from "@/pages/TimesheetsPage";
import ReimbursementsPage from "@/pages/ReimbursementsPage";
import InvoicesPage from "@/pages/InvoicesPage";
import AccountingPage from "@/pages/AccountingPage";
import CalendarPage from "@/pages/CalendarPage";
import ProfilePage from "@/pages/ProfilePage";
import ActivityLogPage from "@/pages/ActivityLogPage";
import StatusUpdatesPage from "@/pages/StatusUpdatesPage";
import ResourcesPage from "@/pages/ResourcesPage";
import Mandatory2FASetup from "@/pages/Mandatory2FASetup";
import SystemLogsPage from "@/pages/SystemLogsPage";
import InactiveContractsPage from "@/pages/InactiveContractsPage";
import DigitalMarketingOverviewPage from "@/pages/digital-marketing/OverviewPage";
import SEOWorkspacePage from "@/pages/digital-marketing/SEOWorkspacePage";
import ContentSocialPage from "@/pages/digital-marketing/ContentSocialPage";
import AnalyticsResearchPage from "@/pages/digital-marketing/AnalyticsResearchPage";
import LibraryPage from "@/pages/LibraryPage";

function ProtectedRoute({ children }) {
  const { user, loading, requires2FASetup } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen bg-background"><div className="animate-pulse text-primary font-heading text-xl">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (requires2FASetup) return <Mandatory2FASetup />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen bg-background"><div className="animate-pulse text-primary font-heading text-xl">Loading...</div></div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="proposals" element={<ProposalsPage />} />
        <Route path="contracts" element={<ContractsPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/:id" element={<ProjectDetailPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="timesheets" element={<TimesheetsPage />} />
        <Route path="reimbursements" element={<ReimbursementsPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="accounting" element={<AccountingPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="activity-log" element={<ActivityLogPage />} />
        <Route path="status-updates" element={<StatusUpdatesPage />} />
        <Route path="resources" element={<ResourcesPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="system-logs" element={<SystemLogsPage />} />
        <Route path="contracts/inactive" element={<InactiveContractsPage />} />
        <Route path="digital-marketing" element={<DigitalMarketingOverviewPage />} />
        <Route path="digital-marketing/seo-workspace" element={<SEOWorkspacePage />} />
        <Route path="digital-marketing/content-social" element={<ContentSocialPage />} />
        <Route path="digital-marketing/analytics" element={<AnalyticsResearchPage />} />
        <Route path="library" element={<LibraryPage />} />
      </Route>
    </Routes>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="top-right" richColors closeButton />;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <ThemedToaster />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
